// src/pages/profile/UserSettings.tsx
import React, { useState, useEffect } from 'react';
import {
  Bell, Globe, Shield, Save,
  ToggleLeft, ToggleRight, Lock, User,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select, Loading } from '../../components/ui';
import { authService } from '../../services/auth';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface UserSettingsData {
  email_notifications: boolean;
  browser_notifications: boolean;
  language: string;
  timezone: string;
}

const UserSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<UserSettingsData>({
    email_notifications: true,
    browser_notifications: true,
    language: 'id',
    timezone: 'Asia/Jakarta',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await authService.getSettings();
      setSettings(res);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authService.updateSettings(settings);
      toast.success('Pengaturan berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof UserSettingsData, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const ToggleSwitch: React.FC<{
    value: boolean;
    onChange: (v: boolean) => void;
    label: string;
    description?: string;
    icon?: React.ReactNode;
  }> = ({ value, onChange, label, description, icon }) => (
    <div className="flex items-center justify-between py-3 border-b border-neutral-border last:border-0">
      <div className="flex items-center gap-3">
        {icon && <div className="text-teal-main">{icon}</div>}
        <div>
          <p className="text-sm font-medium text-neutral-text-primary">{label}</p>
          {description && <p className="text-xs text-neutral-text-secondary mt-0.5">{description}</p>}
        </div>
      </div>
      <button onClick={() => onChange(!value)} className="shrink-0">
        {value ? (
          <ToggleRight className="w-10 h-6 text-teal-main" />
        ) : (
          <ToggleLeft className="w-10 h-6 text-neutral-text-secondary" />
        )}
      </button>
    </div>
  );

  if (loading) {
    return <Loading variant="page" message="Memuat pengaturan..." />;
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Profil', href: '/profile' },
        { label: 'Pengaturan' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Pengaturan</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Kelola preferensi akun Anda</p>
        </div>
        <Button variant="primary" onClick={handleSave} loading={saving} icon={<Save className="w-4 h-4" />}>
          Simpan Perubahan
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Menu */}
        <div className="space-y-2">
          <Link
            to="/profile"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-text-secondary hover:bg-neutral-bg transition-colors"
          >
            <User className="w-5 h-5" />
            <span className="font-medium">Profil Saya</span>
          </Link>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-teal-main text-white">
            <Shield className="w-5 h-5" />
            <span className="font-medium">Pengaturan</span>
          </div>
          <Link
            to="/change-password"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-text-secondary hover:bg-neutral-bg transition-colors"
          >
            <Lock className="w-5 h-5" />
            <span className="font-medium">Ubah Password</span>
          </Link>
        </div>

        {/* Right Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Notifications */}
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <Bell className="w-5 h-5 text-teal-main" />
              <h3 className="text-base font-bold text-teal-dark">Notifikasi</h3>
            </div>
            <div className="space-y-0">
              <ToggleSwitch
                value={settings.email_notifications}
                onChange={(v) => updateSetting('email_notifications', v)}
                label="Notifikasi Email"
                description="Terima update via email untuk aktivitas penting"
                icon={<Bell className="w-4 h-4" />}
              />
              <ToggleSwitch
                value={settings.browser_notifications}
                onChange={(v) => updateSetting('browser_notifications', v)}
                label="Notifikasi Aplikasi"
                description="Terima notifikasi langsung di aplikasi web"
                icon={<Bell className="w-4 h-4" />}
              />
            </div>
          </Card>

          {/* Language & Region */}
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-5 h-5 text-teal-main" />
              <h3 className="text-base font-bold text-teal-dark">Bahasa & Wilayah</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                  Bahasa
                </label>
                <Select
                  options={[
                    { value: 'id', label: 'Bahasa Indonesia' },
                    { value: 'en', label: 'English' },
                  ]}
                  value={settings.language}
                  onChange={(v) => updateSetting('language', v)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                  Zona Waktu
                </label>
                <Select
                  options={[
                    { value: 'Asia/Jakarta', label: 'WIB (Jakarta)' },
                    { value: 'Asia/Makassar', label: 'WITA (Makassar)' },
                    { value: 'Asia/Jayapura', label: 'WIT (Jayapura)' },
                  ]}
                  value={settings.timezone}
                  onChange={(v) => updateSetting('timezone', v)}
                />
              </div>
            </div>
          </Card>

          {/* Quick Links */}
          <Card>
            <h3 className="text-base font-bold text-teal-dark mb-4">Tautan Cepat</h3>
            <div className="space-y-3">
              <Link
                to="/profile"
                className="flex items-center justify-between p-3 rounded-lg bg-neutral-bg hover:bg-neutral-border transition-colors"
              >
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-teal-main" />
                  <div>
                    <p className="text-sm font-medium text-neutral-text-primary">Edit Profil</p>
                    <p className="text-xs text-neutral-text-secondary">Ubah nama, foto, dan info kontak</p>
                  </div>
                </div>
              </Link>
              <Link
                to="/change-password"
                className="flex items-center justify-between p-3 rounded-lg bg-neutral-bg hover:bg-neutral-border transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-teal-main" />
                  <div>
                    <p className="text-sm font-medium text-neutral-text-primary">Ubah Password</p>
                    <p className="text-xs text-neutral-text-secondary">Perbarui password akun Anda</p>
                  </div>
                </div>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
